/**
 * One zone row of the draft pack — the unit the brief allows to be confirmed
 * in bulk, because one zone row is one row of the BZO's parameter table and a
 * human can check it in a single look at the page.
 *
 * The row collapses to a summary and opens into the side-by-side: read values
 * on the left, the source passage on the right. Two consequences of that layout
 * are deliberate and load-bearing:
 *
 *  · Every control that records a confirmation lives in the RIGHT column,
 *    under the citation it will record. There is no way to press "confirm"
 *    with the source passage off screen, because the button is inside the panel
 *    that shows it.
 *  · «Ganze Zeile als stimmt markieren» only MARKS. Committing is still a
 *    separate, explicit act — so a blanket confirmation costs two deliberate
 *    clicks, while correcting one parameter costs one keystroke in a field that
 *    is already live. Correction is not the harder path.
 *
 * There is no control anywhere that confirms more than this one row. The
 * backend refuses one too (422), and a test asserts the absence of the
 * affordance rather than trusting the layout to stay this way.
 *
 * `readOnly` (every non-founder session, Founder decision 2026-09-16): the row
 * still opens into the side-by-side — value, page, passage — but nothing in it
 * records a decision, and the control column says who does. The backend
 * answers 403 to the same session, so this is the UI keeping a promise the
 * server already keeps, not the only line of defence.
 */
import { Fragment, useMemo, useState } from 'react'
import { CheckCheck, ChevronDown, ChevronRight, ShieldCheck } from 'lucide-react'

import { api, type ZoneVerifyItem } from '@/api/client'
import { ErrorNote } from '@/components/ErrorNote'
import { Icon } from '@/components/Icon'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useI18n } from '@/i18n'

import { DispositionField, type Decision } from './DispositionField'
import { SourcePanel } from './SourcePanel'
import {
  citationFor,
  entryNote,
  formatValue,
  isCheckable,
  parseValue,
  sameCitation,
} from './rulebase'
import { ZONE_ROW_KEY, type Citation, type DraftPack, type DraftZone } from '@/api/types'

export function ZoneRowCard({
  workspaceId,
  packId,
  zone,
  onUpdated,
  readOnly = false,
}: {
  workspaceId: string
  packId: string
  zone: DraftZone
  onUpdated: (pack: DraftPack) => void
  readOnly?: boolean
}) {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)
  const [decisions, setDecisions] = useState<Record<string, Decision>>({})
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const notRead = t('verify.notRead')
  const yes = t('common.yes')
  const no = t('common.no')

  /**
   * Everything in this row that a human can decide about: the row itself first
   * — confirming it is what takes the Gemeinde out of indicative mode, so it is
   * an act of its own — then its parameters.
   */
  const entries = useMemo(() => {
    const rowCitation = (zone as { citation?: Citation | null }).citation ?? null
    const list: {
      key: string
      label: string
      hint?: string
      note?: string | null
      extracted: string
      unit?: string | null
      citation: Citation | null
      verified: boolean
    }[] = [
      {
        key: ZONE_ROW_KEY,
        label: t('verify.zoneRowItem'),
        hint: t('verify.zoneRowItemHint'),
        extracted: zone.code,
        citation: isCheckable(rowCitation) ? rowCitation : null,
        verified: zone.status === 'verified',
      },
    ]
    for (const parameter of zone.parameters ?? []) {
      list.push({
        key: parameter.key,
        label: parameter.label_de,
        note: entryNote(parameter, t),
        extracted: formatValue(parameter.value, notRead, yes, no),
        unit: parameter.unit ?? null,
        citation: citationFor(zone, parameter),
        verified: parameter.status === 'verified',
      })
    }
    return list
  }, [zone, t, notRead, yes, no])

  const decidable = entries.filter((entry) => entry.citation !== null)

  const verifiedCount = entries.filter((entry) => entry.verified).length
  const chosen = Object.entries(decisions)
  const counts = {
    confirmed: chosen.filter(([, d]) => d.disposition === 'confirmed').length,
    corrected: chosen.filter(([, d]) => d.disposition === 'corrected').length,
    unreadable: chosen.filter(([, d]) => d.disposition === 'unreadable').length,
  }
  const incompleteCorrection = chosen.some(
    ([, d]) => d.disposition === 'corrected' && d.raw.trim() === '',
  )

  const setDecision = (key: string, next: Decision | undefined) => {
    setSaved(false)
    setDecisions((current) => {
      const copy = { ...current }
      if (next) copy[key] = next
      else delete copy[key]
      return copy
    })
  }

  /** The permitted bulk: marks this row, and only this row. */
  const markRowConfirmed = () => {
    setSaved(false)
    const next: Record<string, Decision> = { ...decisions }
    for (const entry of decidable) next[entry.key] = { disposition: 'confirmed', raw: '' }
    setDecisions(next)
  }

  const submit = async () => {
    if (chosen.length === 0 || incompleteCorrection) return
    setBusy(true)
    setError(null)
    try {
      const items: ZoneVerifyItem[] = []
      for (const entry of decidable) {
        const decision = decisions[entry.key]
        if (!decision || !entry.citation) continue
        items.push({
          key: entry.key,
          disposition: decision.disposition,
          // `confirmed` sends no value at all; `unreadable` sends null — the
          // recorded fact is "not readable", never a zero and never the
          // extraction's guess promoted to a human decision.
          ...(decision.disposition === 'corrected'
            ? { value: parseValue(decision.raw) }
            : decision.disposition === 'unreadable'
              ? { value: null }
              : {}),
          citation: entry.citation,
        })
      }
      const pack = await api.verifyZoneRow(workspaceId, packId, zone.id, items)
      setDecisions({})
      setSaved(true)
      onUpdated(pack)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }

  const panelId = `zone-panel-${zone.id}`
  const verified = zone.status === 'verified'

  return (
    <li>
      <div
        className={[
          'rounded-md border bg-white',
          verified ? 'border-border' : 'border-dashed border-border-strong',
        ].join(' ')}
        data-testid="zone-row"
        data-zone={zone.id}
        data-status={zone.status}
      >
        <h3 className="m-0">
          <button
            type="button"
            aria-expanded={open}
            aria-controls={panelId}
            onClick={() => setOpen((value) => !value)}
            className="flex w-full flex-wrap items-center gap-3 px-6 py-4 text-left"
          >
            <Icon icon={open ? ChevronDown : ChevronRight} className="text-muted" />
            <span className="num text-[15px] font-semibold text-ink">{zone.code}</span>
            {zone.label ? <span className="text-[13px] text-muted">{zone.label}</span> : null}
            <Badge variant={verified ? 'info' : 'indicative'}>
              {verified ? <Icon icon={ShieldCheck} /> : null}
              {t(`verify.status.${zone.status}`)}
            </Badge>
            <span className="num ms-auto text-[12px] text-muted">
              {t('verify.progress', { verified: verifiedCount, total: entries.length })}
            </span>
            <span className="sr-only">{open ? t('verify.collapse') : t('verify.expand')}</span>
          </button>
        </h3>

        {open ? (
          <div id={panelId} className="border-t border-border px-6 py-5">
            {/*
             * Side by side, PER ENTRY — not two stacked columns.
             *
             * One grid, two tracks, one implicit row per entry: the value on the
             * left and the passage it came from directly opposite it. Stacking
             * six fields against a separate list of six snippets looks like the
             * same layout and reads completely differently — the reader has to
             * count down both columns to pair them, and that is exactly the
             * moment somebody stops checking and starts clicking «stimmt».
             *
             * These are columns, not landmarks: two named `region`s per row,
             * repeated down the page, is a worse screen-reader experience than
             * none (and axe flags the duplicate names).
             */}
            <div className="grid gap-x-8 md:grid-cols-2">
              <h4 className="pb-2 text-[13px] font-semibold text-ink-2">
                {t('verify.extracted')}
              </h4>
              <h4 className="hidden pb-2 text-[13px] font-semibold text-ink-2 md:block">
                {t('verify.sourceTitle')}
              </h4>

              {entries.map((entry, index) => {
                // A shared passage — one BZO table row covering several
                // parameters — is shown once and then referred back to, so the
                // reader sees that it really is one line of the document.
                const previous = index > 0 ? entries[index - 1].citation : null
                const repeated = entry.citation !== null && sameCitation(entry.citation, previous)
                return (
                  <Fragment key={entry.key}>
                    <DispositionField
                      name={`${zone.id}-${entry.key}`}
                      label={entry.label}
                      hint={entry.hint}
                      note={entry.note}
                      extracted={entry.extracted}
                      unit={entry.unit}
                      hasCitation={entry.citation !== null}
                      page={
                        (entry.citation as unknown as { page?: number | null } | null)?.page ?? null
                      }
                      decision={decisions[entry.key]}
                      onChange={(next) => setDecision(entry.key, next)}
                      readOnly={readOnly}
                    />
                    <div className="border-b border-border py-3 last:border-b-0">
                      {entry.citation === null ? (
                        <p className="text-[12px] text-muted">{t('verify.noCitation')}</p>
                      ) : repeated ? (
                        <p className="text-[12px] text-muted-2">{t('verify.sameSource')}</p>
                      ) : (
                        <SourcePanel
                          groups={[{ citation: entry.citation, covers: [] }]}
                        />
                      )}
                    </div>
                  </Fragment>
                )
              })}
            </div>

            {decidable.length > 0 && readOnly ? (
              <div className="mt-5 grid gap-x-8 md:grid-cols-2">
                <div className="hidden md:block" />
                <p
                  className="border-t border-border pt-4 text-[12px] text-muted"
                  data-testid="row-founder-only"
                >
                  {t('verify.founderOnlyRow')}
                </p>
              </div>
            ) : decidable.length > 0 ? (
              <div className="mt-5 grid gap-x-8 md:grid-cols-2">
                <div className="hidden md:block" />
                {/* Every control that records a confirmation sits in the column
                    the passages are in — you cannot reach «bestätigen» with the
                    source off screen, because the button lives beside it. */}
                <div className="flex flex-col gap-3 border-t border-border pt-4">
                  <Button
                    variant="secondary"
                    size="sm"
                    className="self-start"
                    onClick={markRowConfirmed}
                    data-testid="mark-row-confirmed"
                  >
                    <Icon icon={CheckCheck} />
                    {t('verify.markAllConfirmed')}
                  </Button>
                  <p className="text-[12px] text-muted">{t('verify.markAllHint')}</p>

                  <p className="num text-[12px] text-muted-2" data-testid="row-breakdown">
                    {counts.confirmed}× {t('verify.confirm')} · {counts.corrected}×{' '}
                    {t('verify.correct')} · {counts.unreadable}× {t('verify.unreadable')}
                  </p>

                  {incompleteCorrection ? (
                    <p role="alert" className="text-[12px] text-signal-red-text">
                      {t('verify.correctionNeedsValue')}
                    </p>
                  ) : null}

                  <Button
                    className="self-start"
                    disabled={busy || chosen.length === 0 || incompleteCorrection}
                    onClick={() => void submit()}
                    data-testid="commit-zone-row"
                  >
                    {busy
                      ? t('verify.submitting')
                      : chosen.length === 0
                        ? t('verify.submitRowNone')
                        : chosen.length === 1
                          ? t('verify.submitRowOne')
                          : t('verify.submitRow', { count: chosen.length })}
                  </Button>

                  {saved ? (
                    <p role="status" className="text-[12px] text-muted" data-testid="row-saved">
                      {t('verify.rowSaved')}
                    </p>
                  ) : null}
                  {error ? <ErrorNote message={`${t('verify.rowError')}: ${error}`} /> : null}
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </li>
  )
}
